import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = <div className="space-y-6 text-brown-light/80 text-[15px] leading-relaxed" data-id="element-191">
    <div data-id="element-192">
      <h3 className="font-serif text-2xl text-brown mb-1" data-id="element-193">Privacy Policy</h3>
      <p className="text-xs text-brown-light/40 uppercase tracking-[0.2em]" data-id="element-194">
        Last updated: {new Date().toLocaleDateString()}
      </p>
    </div>
    <div data-id="element-195">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-196">
        1. Information We Collect
      </p>
      <p data-id="element-197">
        We only collect information necessary to process your purchase and
        deliver your digital product. This includes your email address and
        payment information (processed securely by our payment provider).
      </p>
    </div>
    <div data-id="element-198">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-199">
        2. How We Use Your Information
      </p>
      <p data-id="element-200">
        We use your email to send you the product download link and occasional
        updates about the product. We do not sell your data to third parties.
      </p>
    </div>
    <div data-id="element-201">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-202">
        3. Cookies
      </p>
      <p data-id="element-203">
        We use essential cookies to ensure the website functions properly and to
        facilitate the purchase process.
      </p>
    </div>
    <div data-id="element-204">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-205">
        4. Contact
      </p>
      <p data-id="element-206">
        If you have questions about your privacy, please contact the support
        email provided in your purchase receipt.
      </p>
    </div>
  </div>;
const TERMS_CONTENT = <div className="space-y-6 text-brown-light/80 text-[15px] leading-relaxed" data-id="element-207">
    <div data-id="element-208">
      <h3 className="font-serif text-2xl text-brown mb-1" data-id="element-209">Terms of Service</h3>
      <p className="text-xs text-brown-light/40 uppercase tracking-[0.2em]" data-id="element-210">
        Last updated: {new Date().toLocaleDateString()}
      </p>
    </div>
    <div data-id="element-211">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-212">
        1. Digital Product Policy
      </p>
      <p data-id="element-213">
        Due to the nature of digital products, all sales are final once the
        download link has been accessed. However, if you have technical issues,
        please contact support.
      </p>
    </div>
    <div data-id="element-214">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-215">
        2. License
      </p>
      <p data-id="element-216">
        Purchase grants you a single personal license to use this product. You
        may not redistribute, resell, or share the files with others.
      </p>
    </div>
    <div data-id="element-217">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-218">
        3. Disclaimer
      </p>
      <p data-id="element-219">
        This product is for educational purposes only. It is not medical advice.
        Always consult with your pediatrician regarding your baby's health and
        sleep habits.
      </p>
    </div>
    <div data-id="element-220">
      <p className="font-serif text-base font-medium text-brown mb-2" data-id="element-221">
        4. Limitation of Liability
      </p>
      <p data-id="element-222">
        We are not liable for any direct, indirect, incidental, or consequential
        damages arising from the use of this product.
      </p>
    </div>
  </div>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  return <>
      <footer className="relative py-12 px-6 bg-cream overflow-hidden" data-id="element-223">
        {/* Top divider */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brown/[0.06] to-transparent" data-id="element-224" />

        <div className="max-w-4xl mx-auto flex flex-col items-center gap-5" data-id="element-225">
          {/* Ornamental mark */}
          <div className="flex items-center gap-3 mb-1" data-id="element-226">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-brown/10" data-id="element-227" />
            <span className="text-brown/15 text-[10px] tracking-[0.3em]" data-id="element-228">
              ✦
            </span>
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-brown/10" data-id="element-229" />
          </div>

          <p className="text-brown-light/40 text-xs tracking-wide" data-id="element-230">
            © {new Date().getFullYear()} The Nap-Trapped Reset. All rights
            reserved.
          </p>

          <div className="flex items-center gap-5 text-xs" data-id="element-231">
            <button onClick={() => setActiveModal('privacy')} className="text-brown-light/50 hover:text-brown/70 transition-colors duration-200 underline underline-offset-4 decoration-brown-light/15 hover:decoration-brown-light/30" data-id="element-232">
              Privacy Policy
            </button>
            <span className="text-brown/10" data-id="element-233">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-brown-light/50 hover:text-brown/70 transition-colors duration-200 underline underline-offset-4 decoration-brown-light/15 hover:decoration-brown-light/30" data-id="element-234">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-235">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-brown/30 backdrop-blur-md z-50" data-id="element-236" />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-237">
              <motion.div initial={{
            opacity: 0,
            scale: 0.96,
            y: 16
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.96,
            y: 16
          }} transition={{
            duration: 0.4,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="w-full max-w-lg max-h-[80vh] rounded-2xl md:rounded-3xl overflow-hidden flex flex-col pointer-events-auto border border-brown/[0.06] relative" style={{
            background: 'linear-gradient(165deg, #FFFFFF 0%, #FAF7F2 60%, #F5F0E8 100%)',
            boxShadow: '0 8px 32px rgba(61,50,41,0.12), 0 24px 64px rgba(61,50,41,0.08)'
          }} data-id="element-238">
                {/* Top accent strip */}
                <div className="h-[3px] w-full" style={{
              background: 'linear-gradient(90deg, transparent 5%, rgba(139,152,119,0.3) 50%, transparent 95%)'
            }} data-id="element-239" />

                {/* Warm top glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-24 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at top, rgba(139,152,119,0.05) 0%, transparent 70%)'
            }} data-id="element-240" />

                {/* Header */}
                <div className="px-6 md:px-8 pt-5 pb-4 flex items-center justify-between relative z-10" data-id="element-241">
                  <div className="flex items-center gap-2" data-id="element-242">
                    <div className="w-6 h-px bg-gradient-to-r from-sage/30 to-transparent" data-id="element-243" />
                    <span className="text-[9px] uppercase tracking-[0.25em] text-brown/30 font-bold" data-id="element-244">
                      {activeModal === 'privacy' ? 'Legal' : 'Legal'}
                    </span>
                  </div>
                  <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-brown/[0.04] hover:bg-brown/[0.08] transition-colors duration-200 text-brown-light/50 hover:text-brown/70" data-id="element-245">
                    <X className="w-4 h-4" strokeWidth={1.5} data-id="element-246" />
                  </button>
                </div>

                {/* Thin divider */}
                <div className="mx-6 md:mx-8 h-px bg-gradient-to-r from-transparent via-brown/[0.06] to-transparent" data-id="element-247" />

                {/* Content */}
                <div className="px-6 md:px-8 py-6 md:py-8 overflow-y-auto relative z-10" data-id="element-248">
                  {activeModal === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT}
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </>;
}